<template>
    <section class="relative overflow-hidden bg-background py-16 lg:py-24">
        <div
            v-if="content.bg_image"
            class="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-[0.12]"
            :style="{ backgroundImage: `url(${content.bg_image})`, filter: 'grayscale(100%)' }"
        />
        <div
            class="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-accent/5 blur-3xl -translate-y-1/2 translate-x-1/3"
        />

        <div class="relative z-10 container mx-auto px-4">
            <div class="max-w-md mx-auto bg-card rounded-2xl p-8 shadow-lg border border-border">
                <h2 class="text-2xl font-bold text-foreground mb-6">
                    {{ content.title || "Авторизация" }}
                </h2>

                <div v-if="tabs.length > 1" class="flex gap-2 mb-6">
                    <button
                        v-for="t in tabs"
                        :key="t.key"
                        type="button"
                        class="px-4 py-2 rounded-md text-sm font-semibold transition-colors"
                        :class="
                            tab === t.key
                                ? 'bg-accent text-accent-foreground'
                                : 'bg-secondary text-foreground hover:bg-accent/10'
                        "
                        @click="switchTab(t.key)"
                    >
                        {{ t.label }}
                    </button>
                </div>

                <!-- Вход -->
                <form v-if="tab === 'login'" class="space-y-4" @submit.prevent="submitLogin">
                    <div>
                        <label class="block text-sm font-medium text-foreground mb-1.5" for="landing-auth-login">
                            {{ content.login_label || "Имя пользователя" }}
                        </label>
                        <input
                            id="landing-auth-login"
                            v-model="login.login"
                            type="text"
                            name="login"
                            required
                            class="w-full px-4 py-2.5 rounded-md border border-border bg-background text-foreground"
                        />
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-foreground mb-1.5" for="landing-auth-password">
                            {{ content.password_label || "Пароль" }}
                        </label>
                        <input
                            id="landing-auth-password"
                            v-model="login.password"
                            type="password"
                            name="password"
                            required
                            class="w-full px-4 py-2.5 rounded-md border border-border bg-background text-foreground"
                        />
                    </div>
                    <label class="flex items-center gap-2 text-sm text-foreground/80 select-none">
                        <input v-model="login.remember" type="checkbox" name="remember" class="accent-current" />
                        {{ content.remember_label || "Запомнить меня на этом компьютере" }}
                    </label>
                    <p v-if="error" class="text-sm text-destructive">{{ error }}</p>
                    <div class="flex items-center justify-between gap-4 pt-1">
                        <a href="/password/reset" class="text-sm text-accent hover:underline">
                            {{ content.forgot_label || "Забыли пароль?" }}
                        </a>
                        <button
                            type="submit"
                            :disabled="sending"
                            class="px-6 py-2.5 rounded-md font-semibold bg-accent text-accent-foreground hover:bg-accent/90 transition-colors disabled:opacity-60"
                        >
                            {{ sending ? "Вход..." : content.submit_label || "Войти" }}
                        </button>
                    </div>
                </form>

                <!-- Регистрация -->
                <div v-else-if="tab === 'register'" class="space-y-6">
                    <p class="text-sm text-foreground/80 leading-relaxed">
                        {{ content.register_text || "Зарегистрируйте организацию и начните работать с системой." }}
                    </p>
                    <a
                        href="/register"
                        class="inline-flex px-6 py-2.5 rounded-md font-semibold bg-accent text-accent-foreground hover:bg-accent/90 transition-colors"
                        @click="guardPreview"
                    >
                        {{ content.register_button_label || "Перейти к регистрации" }}
                    </a>
                </div>

                <!-- Демо-вход -->
                <form v-else-if="tab === 'demo'" class="space-y-4" @submit.prevent="submitDemo">
                    <b class="block text-foreground">{{ content.demo_title || "Демонстрационный режим" }}</b>
                    <p
                        v-for="(line, i) in demoDescriptionLines"
                        :key="i"
                        class="text-sm text-foreground/80 leading-relaxed"
                    >
                        {{ line }}
                    </p>
                    <div>
                        <label class="block text-sm font-medium text-foreground mb-1.5" for="landing-auth-demo-email">
                            E-mail
                        </label>
                        <input
                            id="landing-auth-demo-email"
                            v-model="demo.email"
                            type="email"
                            name="email"
                            required
                            class="w-full px-4 py-2.5 rounded-md border border-border bg-background text-foreground"
                        />
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-foreground mb-1.5" for="landing-auth-demo-phone">
                            Телефон
                        </label>
                        <input
                            id="landing-auth-demo-phone"
                            v-model="demo.phone"
                            type="tel"
                            name="phone"
                            required
                            placeholder="+7 ..."
                            class="w-full px-4 py-2.5 rounded-md border border-border bg-background text-foreground"
                        />
                    </div>
                    <ConsentCheckbox v-if="landingConsent.demo" :text="landingConsent.demo" />
                    <p v-if="error" class="text-sm text-destructive">{{ error }}</p>
                    <div class="flex justify-end pt-1">
                        <button
                            type="submit"
                            :disabled="sending"
                            class="px-6 py-2.5 rounded-md font-semibold bg-accent text-accent-foreground hover:bg-accent/90 transition-colors disabled:opacity-60"
                        >
                            {{ sending ? "Вход..." : content.demo_submit_label || "Войти" }}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </section>
</template>

<script>
import ConsentCheckbox from "./ConsentCheckbox.vue";

export default {
    name: "AuthBlock",
    components: { ConsentCheckbox },
    props: { content: { type: Object, default: () => ({}) } },
    inject: {
        // настройки домена (LandingResource -> state.auth); превью подставляет свои
        landingAuth: { default: () => ({}) },
        landingConsent: { default: () => ({}) },
        landingPreview: { default: false },
    },
    data() {
        return {
            tab: "login",
            sending: false,
            error: "",
            login: { login: "", password: "", remember: false },
            demo: { email: "", phone: "" },
        };
    },
    computed: {
        tabs() {
            const tabs = [{ key: "login", label: this.content.tab_login || "Вход" }];
            if (this.landingAuth.allow_register) {
                tabs.push({ key: "register", label: this.content.tab_register || "Регистрация" });
            }
            if (this.landingAuth.allow_demo) {
                tabs.push({ key: "demo", label: this.content.tab_demo || "Демо-вход" });
            }

            return tabs;
        },
        demoDescriptionLines() {
            return (this.content.demo_description || "").split("\n").filter((line) => line.trim());
        },
    },
    methods: {
        switchTab(key) {
            this.tab = key;
            this.error = "";
        },
        guardPreview(event) {
            if (this.landingPreview) {
                event.preventDefault();
            }
        },
        async submitLogin() {
            await this.post("/login", this.login, "Не удалось войти. Попробуйте ещё раз.");
        },
        async submitDemo() {
            await this.post("/demo", this.demo, "Не удалось выполнить демо-вход. Попробуйте ещё раз.");
        },
        /**
         * Классические маршруты аутентификации отвечают на JSON-запрос:
         * 204 — успех (вход), 302 — успех с переходом (демо),
         * 422/429 — ошибки валидации (в т.ч. пользователь чужого домена)
         * либо блокировка повторных попыток.
         */
        async post(url, payload, fallbackError) {
            if (this.landingPreview || this.sending) {
                return;
            }

            this.sending = true;
            this.error = "";
            try {
                const response = await fetch(url, {
                    method: "POST",
                    credentials: "same-origin",
                    headers: { "Content-Type": "application/json", Accept: "application/json" },
                    body: JSON.stringify(payload),
                });

                if (response.status === 204) {
                    window.location.href = "/corporate";

                    return;
                }
                if (response.redirected) {
                    window.location.href = response.url;

                    return;
                }
                if (response.status === 422 || response.status === 429) {
                    const data = await response.json();
                    this.error = Object.values(data.errors || {}).flat()[0] || data.message || fallbackError;

                    return;
                }

                throw new Error(fallbackError);
            } catch (error) {
                this.error = error.message || fallbackError;
            } finally {
                this.sending = false;
            }
        },
    },
};
</script>
